import { CRUDAction, FeatureKey } from 'common/constants/permission'
import useMedia from 'common/hooks/useMedia'
import { fNumber } from 'common/utils/formatNumber'
import { useGetFoodToday } from 'modules/food/api/queries'
import FoodChart from 'modules/food/components/FoodChart'
import { selectedMealDateState } from 'modules/food/stores/atom'
import { useIsShrimpSpecie } from 'modules/pond/api/selector'
import { usePermitToActionFeature } from 'modules/user/api/selector'
import { useTranslation } from 'react-i18next'
import { useSetRecoilState } from 'recoil'

import ContentIcon from '@mui/icons-material/ContentPaste'
import CookieIcon from '@mui/icons-material/Cookie'
import FoodIcon from '@mui/icons-material/Restaurant'
import { Card, Typography, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'

import { RecordFeatureButton } from 'components/RecordFeatureButton'

import FishFoodTable from '../FishFoodTable'
import FoodCard from '../FoodCard'
import FoodTable from '../FoodTable'
import RecordFoodDialog from '../RecordFoodDialog'

interface FoodProps {
  hideButton?: boolean
}

const Food = ({ hideButton }: FoodProps) => {
  const { t } = useTranslation(['food', 'pond'])
  const theme = useTheme()
  const isShrimpSpecie = useIsShrimpSpecie()
  const isMobile = useMedia('sm')

  const setDateState = useSetRecoilState(selectedMealDateState)
  const showRecordBtn = usePermitToActionFeature([CRUDAction.Create], FeatureKey.Food)

  const { data: foodToday, isFetching: isFoodTodayFetching } = useGetFoodToday()

  const getPercentColor = (value: number) => {
    if (value < 0) return theme.palette.error.main
    if (value > 0) return theme.palette.primary.main
    return undefined
  }

  return (
    <Box sx={{ py: isMobile ? 0 : 3 }}>
      <RecordFeatureButton
        buttonText={t('food:food.record')}
        dialog={({ open, onClose }) => <RecordFoodDialog open={open} onClose={onClose} />}
        onAfterOpen={() => {
          setDateState(new Date())
        }}
        showButton={showRecordBtn}
      />
      <Grid item xs={12}>
        <Card
          sx={{
            width: '100%',
            height: '100%',
            p: 3,
          }}
        >
          <Typography mb={4} fontSize={18} fontWeight={600}>
            {t('food:food')}
          </Typography>
          {!isFoodTodayFetching && (
            <>
              {isMobile ? (
                <Box mb={2}>
                  <FoodCard
                    icon={FoodIcon}
                    label={t('food:food.today')}
                    value={foodToday?.totalFood ?? 0}
                    unit={t('food:kilo')}
                    padding={1}
                    textSize={14}
                    numberSize={28}
                    unitSize={14}
                  >
                    <Box justifyContent="space-between" display="flex" alignItems="center">
                      <Typography mb={1} fontSize={14} color={theme.palette.grey[600]}>
                        {t('food:food.accumulate')}
                      </Typography>

                      <Typography mb={1} fontSize={20} fontWeight={600}>
                        {fNumber(foodToday?.accumulatedFood ?? 0)} {t('food:kilo')}
                      </Typography>
                    </Box>

                    <Box justifyContent="space-between" display="flex" alignItems="center">
                      <Typography mb={1} fontSize={14} color={theme.palette.grey[600]}>
                        {t('food:food.yesterday')}
                      </Typography>

                      <Typography
                        mb={1}
                        fontSize={20}
                        fontWeight={600}
                        color={getPercentColor(foodToday?.foodConsumptionChangePercentage ?? 0)}
                      >
                        {(foodToday?.foodConsumptionChangePercentage ?? 0) > 0 && '+'}{' '}
                        {fNumber(foodToday?.foodConsumptionChangePercentage ?? 0)} %
                      </Typography>
                    </Box>
                  </FoodCard>
                </Box>
              ) : (
                <Grid container spacing={2} mb={2}>
                  <FoodCard
                    icon={FoodIcon}
                    label={t('food:food.today')}
                    value={foodToday?.totalFood ?? 0}
                    unit={t('food:kilo')}
                  />
                  <FoodCard
                    icon={CookieIcon}
                    label={t('food:food.accumulate')}
                    value={foodToday?.accumulatedFood ?? 0}
                    unit={t('food:kilo')}
                    disabledColor={true}
                  />
                  <FoodCard
                    icon={ContentIcon}
                    label={t('food:food.yesterday')}
                    value={foodToday?.foodConsumptionChangePercentage ?? 0}
                    isPercentage
                  />
                </Grid>
              )}
            </>
          )}
          <FoodChart />
        </Card>
      </Grid>

      <Grid container sx={{ mt: 4 }}>
        <Grid item xs={12}>
          {isShrimpSpecie ? <FoodTable /> : <FishFoodTable />}
        </Grid>
      </Grid>
    </Box>
  )
}

export default Food
